import { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { 
  getColorScheme, 
  getBannerAndPropertyLogoUrls, 
  fetchThemeCSS,
  applyCssStyles,
  removeCssStyles,
  setTraditionalTheme,
  getTraditionalTheme,
  mapThemeResponseToPropertyInfo,
  useColorScheme,
  useBannerAndLogoUrls,
  useThemeCSS
} from '@/lib/services/themeService'
import { themeConfig} from '@/config/theme'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/lib/auth'
import type { PropertyInfo, ThemeState } from '@/types/theme'
import { localStorageVariables } from '@/types/localStorageVariables'
import { yardiTranslationConfig } from 'yardi-react-translation/translationService'
import { getPropertyId } from '@/hooks/useApiSequencing'
import { applyFavicon } from '@/lib/faviconUtils'

const THEME_STYLE_ID = 'custom-theme-styles'
const TRADITIONAL_THEME_LINK_ID = 'traditional-theme-link'

// System routes that should not be treated as property names
const SYSTEM_ROUTES = [
  'auth',
  'dashboard', 
  'settings',
  'login',
  'theme-demo',
  'cache-test',
  'employee',
  'landing',
  'protected',
  'notifications',
  'localization-test',
  'syncfusion-example'
] as const

export function useTheme() {
  const location = useLocation()
  const params = useParams<{ property?: string }>()
  const { toast } = useToast()
  const { user, isLoading: isAuthLoading } = useAuth()
  
  // Property information status tracking (equivalent to Angular BehaviorSubject)
  const propertyInfoStatusRef = useRef<boolean>(false)
  const [propertyInfoStatus, setPropertyInfoStatus] = useState<boolean>(false)
  
  // Track if initial theme loading is complete (critical for API call sequencing)
  const [isInitialThemeLoadComplete, setIsInitialThemeLoadComplete] = useState<boolean>(false)
  
  // Track auth loading timeout for public pages
  const authLoadingStartRef = useRef<number>(Date.now())
  
  const [state, setState] = useState<ThemeState>({
    isLoading: false,
    siteName: null,
    cssUrl: null,
    propertyInfo: null,
    error: null,
    isCustomTheme: false,
    propertyInfoLoaded: false,
    propertyLogoUrl: themeConfig.logoImageURL,
    propertyBannerImageUrl: themeConfig.bannerImageURL
  })

  // Extract property name from route params (matches Angular behavior)
  const getPropertyFromRoute = useCallback((): string | null => {
    // First, try to get from React Router params
    if (params.property) {
      return params.property
    }
    
    // Fallback: extract from pathname for routes that don't use :property parameter
    const segments = location.pathname.split('/').filter(Boolean)
    
    // Handle specific route patterns
    if (segments.length >= 4 && segments[0] === 'onlineleasingstudent' && segments[1] === 'content3') {
      // Pattern: /onlineleasingstudent/content3/access/:property/... or /onlineleasingstudent/content3/:property/...
      const propertySegment = segments[2] === 'access' ? segments[3] : segments[2]
      if (propertySegment && !SYSTEM_ROUTES.includes(propertySegment as any)) {
        return propertySegment
      }
    }
    
    // Handle simple routes like /:property/...
    if (segments.length > 0) {
      const firstSegment = segments[0]
      if (firstSegment && !SYSTEM_ROUTES.includes(firstSegment as any)) {
        return firstSegment
      }
    }
    
    return null
  }, [params.property, location.pathname])

  const currentProperty = getPropertyFromRoute()

  // Only log when property actually changes
  const previousProperty = useRef<string | null>(null)
  useEffect(() => {
    if (previousProperty.current !== currentProperty) {
      console.log('🏠 Current Property changed:', { from: previousProperty.current, to: currentProperty })
      previousProperty.current = currentProperty
    }
  }, [currentProperty])

  // Check if current route is a system route (not a property)
  const isSystemRoute = useCallback((pathname: string): boolean => {
    const segments = pathname.split('/').filter(Boolean)
    
    // Special handling for property-specific routes
    // These patterns should NOT be treated as system routes even if they contain system-like segments
    const isPropertySpecificRoute = 
      // Pattern: /onlineleasingstudent/content3/access/:property/... or /onlineleasingstudent/content3/:property/...
      (segments.length >= 4 && segments[0] === 'onlineleasingstudent' && segments[1] === 'content3') ||
      // Pattern: /:property/ole/... or /:property/auth/...
      (segments.length >= 2 && (segments[1] === 'ole' || segments[1] === 'auth'))
    
    if (isPropertySpecificRoute) {
      return false
    }
    
    // For non-property-specific routes, check if it's a direct system route
    // Only treat as system route if the first segment is a system route
    const isDirectSystemRoute = segments.length >= 1 && SYSTEM_ROUTES.includes(segments[0] as any)
    
    return isDirectSystemRoute
  }, [])

  // Check if current property is different from stored one
  const isNewProperty = useCallback(() => {
    const storedSiteName = localStorage.getItem(localStorageVariables.sitename)
    
    // If current route is a system route, it's not a new property
    if (isSystemRoute(location.pathname)) {
      return false
    }
    
    // If no current property, it's not new
    if (!currentProperty) {
      return false
    }
    
    // If stored site name was a system route, and current is a real property, it's new
    if (storedSiteName && SYSTEM_ROUTES.includes(storedSiteName as any) && currentProperty) {
      return true
    }
    
    // Both are properties, check if they're different
    const isNew = currentProperty && storedSiteName && currentProperty !== storedSiteName && 
      !SYSTEM_ROUTES.includes(storedSiteName as any)
    
    return isNew
  }, [currentProperty, location.pathname, isSystemRoute])

  // Check if we should skip API call (user logged in with stored theme data)
  const shouldSkipApiCall = useCallback(() => {
    // Only log when conditions change to reduce noise
    if (isAuthLoading) {
      const timeWaiting = Date.now() - authLoadingStartRef.current
      
      // For public pages (routes with /access/), be more aggressive about API calls
      const isPublicPage = location.pathname.includes('/access/')
      const timeout = isPublicPage ? 1000 : 5000 // 1 second for public pages, 5 seconds for others
      
      if (timeWaiting > timeout) {
        console.log(`⏰ shouldSkipApiCall: false - Auth loading timeout (${timeWaiting}ms), proceeding with API call`)
        return false
      }
      
      // Only log every 500ms to reduce spam
      if (timeWaiting % 500 < 100) {
        console.log(`⏳ shouldSkipApiCall: true - Auth still loading (${timeWaiting}ms)`)
      }
      return true // Wait for auth to load
    }
    
    // Skip API call if it's a new property (we want to load the new theme)
    if (isNewProperty()) {
      console.log('🆕 shouldSkipApiCall: false - New property detected, need fresh API call')
      return false
    }
    
    // Check if we have stored theme data (regardless of auth status)
    const storedTheme = localStorage.getItem(localStorageVariables.customtheme)
    const storedSiteName = localStorage.getItem(localStorageVariables.sitename)
    const storedProperty = localStorage.getItem(localStorageVariables.Property)
    
    // Check if stored data is stale (older than 5 minutes)
    const lastThemeUpdate = localStorage.getItem('lastThemeUpdate')
    const now = Date.now()
    const isStaleData = !lastThemeUpdate || (now - parseInt(lastThemeUpdate)) > (5 * 60 * 1000) // 5 minutes
    
    if (isStaleData) {
      console.log('⏰ shouldSkipApiCall: false - Theme data is stale, need fresh API call')
      return false
    }
    
    // If current route is a system route and we have stored theme, use it
    if (isSystemRoute(location.pathname) && storedTheme && storedSiteName && storedProperty) {
      console.log('🖥️ shouldSkipApiCall: true - System route with stored theme')
      return true
    }
    
    // If we have complete stored data and current property matches stored, use stored data
    if (currentProperty && storedSiteName === currentProperty && storedTheme && storedProperty) {
      console.log('✅ shouldSkipApiCall: true - Current property matches stored data')
      return true
    }
    
    console.log('🚀 shouldSkipApiCall: false - Need fresh API call')
    return false
  }, [isAuthLoading, isNewProperty, currentProperty, location.pathname, isSystemRoute])

  // Reset auth loading timer when auth state changes
  useEffect(() => {
    if (isAuthLoading) {
      authLoadingStartRef.current = Date.now()
    }
  }, [isAuthLoading])

  // Determine if we should enable the API call - THIS IS THE CRITICAL SEQUENCING LOGIC
  const shouldEnableApiCall = !shouldSkipApiCall() && !!currentProperty && !isSystemRoute(location.pathname)
  
  // Only log API decision when it changes
  const previousApiDecision = useRef<boolean | null>(null)
  useEffect(() => {
    if (previousApiDecision.current !== shouldEnableApiCall) {
      console.log('🎯 API Call Decision changed:', {
        from: previousApiDecision.current,
        to: shouldEnableApiCall,
        shouldSkipApiCall: shouldSkipApiCall(),
        currentProperty,
        isSystemRoute: isSystemRoute(location.pathname),
        pathname: location.pathname
      })
      previousApiDecision.current = shouldEnableApiCall
    }
  }, [shouldEnableApiCall, currentProperty, location.pathname])

  // 🚨 CRITICAL: React Query hook for getting color scheme (FIRST API CALL - matches Angular behavior)
  const colorSchemeOptions = useColorScheme(currentProperty, shouldEnableApiCall)
  
  const { 
    data: colorSchemeData, 
    isLoading: isLoadingColorScheme, 
    error: colorSchemeError,
    refetch: refetchColorScheme,
    isSuccess: isColorSchemeSuccess
  } = useQuery(colorSchemeOptions)

  // 🚨 CRITICAL: Second API call only triggers AFTER first API call succeeds
  const propertyId = colorSchemeData?.propertyId || getPropertyId()
  const bannerLogoOptions = useBannerAndLogoUrls(
    currentProperty, 
    propertyId, 
    shouldEnableApiCall && !!propertyId && isColorSchemeSuccess // Wait for color scheme to succeed
  )
  
  const { 
    data: bannerLogoData, 
    isLoading: isLoadingBannerLogo, 
    error: bannerLogoError,
    refetch: refetchBannerLogo
  } = useQuery(bannerLogoOptions)

  // React Query hook for fetching CSS content
  const cssUrl = colorSchemeData?.cssfileURL || localStorage.getItem(localStorageVariables.customtheme)
  const cssOptions = useThemeCSS(cssUrl, !!cssUrl)
  const { 
    data: cssContent, 
    isLoading: isLoadingCSS, 
    error: cssError
  } = useQuery(cssOptions)

  // 🚨 CRITICAL: Public method to check if theme loading is complete
  // This allows other components to wait before making their API calls
  const isThemeReady = useCallback((): boolean => {
    // If we're using stored theme data, it's ready immediately
    if (shouldSkipApiCall()) {
      return isInitialThemeLoadComplete
    }
    
    // For new API calls, wait for color scheme to succeed and property info to be loaded
    return isColorSchemeSuccess && propertyInfoStatus
  }, [shouldSkipApiCall, isInitialThemeLoadComplete, isColorSchemeSuccess, propertyInfoStatus])

  // 🚨 CRITICAL: Public method to wait for theme loading to complete
  // This can be used by other components before making API calls
  const waitForThemeReady = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      if (isThemeReady()) {
        resolve()
        return
      }
      
      // Poll every 100ms until theme is ready
      const checkInterval = setInterval(() => {
        if (isThemeReady()) {
          clearInterval(checkInterval)
          resolve()
        }
      }, 100)
      
      // Timeout after 10 seconds to prevent infinite waiting
      setTimeout(() => {
        clearInterval(checkInterval)
        resolve()
      }, 10000)
    })
  }, [isThemeReady])

  // Property information status getter (equivalent to Angular getPropertyInformationStatus)
  const getPropertyInformationStatus = useCallback(() => {
    return propertyInfoStatus
  }, [propertyInfoStatus])

  // Clear theme and restore defaults
  const clearTheme = useCallback(() => {
    // Remove from localStorage
    localStorage.removeItem(localStorageVariables.customtheme)
    localStorage.removeItem(localStorageVariables.sitename)
    localStorage.removeItem(localStorageVariables.Property)
    localStorage.removeItem(localStorageVariables.PropertyId)
    localStorage.removeItem(localStorageVariables.PropertyLogoUrl)
    localStorage.removeItem(localStorageVariables.PropertyBannerImageUrl)

    // Remove CSS styles
    removeCssStyles(THEME_STYLE_ID)

    // Reset property info status
    propertyInfoStatusRef.current = false
    setPropertyInfoStatus(false)
    setIsInitialThemeLoadComplete(false)

    setState({
      isLoading: false,
      siteName: null,
      cssUrl: null,
      propertyInfo: null,
      error: null,
      isCustomTheme: false,
      propertyInfoLoaded: false,
      propertyLogoUrl: themeConfig.logoImageURL,
      propertyBannerImageUrl: themeConfig.bannerImageURL
    })
  }, [])

  // Initialize from localStorage on mount or when visiting generic routes
  useEffect(() => {
    const storedTheme = localStorage.getItem(localStorageVariables.customtheme)
    const storedSiteName = localStorage.getItem(localStorageVariables.sitename)
    const storedProperty = localStorage.getItem(localStorageVariables.Property)
    const storedPropertyLogoUrl = localStorage.getItem(localStorageVariables.PropertyLogoUrl)
    const storedPropertyBannerUrl = localStorage.getItem(localStorageVariables.PropertyBannerImageUrl)

    // Apply stored theme if available and either:
    // 1. Current route is a system route (like auth, dashboard, settings) and we have stored theme
    // 2. Current property matches stored site name
    const shouldApplyStoredTheme = storedTheme && storedSiteName && storedProperty && 
      (isSystemRoute(location.pathname) || currentProperty === storedSiteName)

    if (shouldApplyStoredTheme) {
      try {
        const propertyInfo: PropertyInfo = JSON.parse(storedProperty)
        
        // Note: CSS fetching is now handled by React Query useThemeCSS hook
        // No need to manually fetch CSS here to avoid duplicate requests
        
        setState(prev => ({
          ...prev,
          siteName: storedSiteName,
          cssUrl: storedTheme,
          propertyInfo,
          isCustomTheme: true,
          propertyInfoLoaded: true,
          propertyLogoUrl: storedPropertyLogoUrl || themeConfig.logoImageURL,
          propertyBannerImageUrl: storedPropertyBannerUrl || themeConfig.bannerImageURL
        }))
        
        propertyInfoStatusRef.current = true
        setPropertyInfoStatus(true)
        setIsInitialThemeLoadComplete(true) // Mark initial load as complete
      } catch (error) {
        console.error('Error parsing stored property info:', error)
        clearTheme()
      }
    } else {
      // If no stored theme is applied, mark as complete so other logic can proceed
      setIsInitialThemeLoadComplete(true)
    }

    // Initialize traditional theme if set
    const traditionalTheme = getTraditionalTheme(themeConfig.defaultTheme)
    if (traditionalTheme !== themeConfig.defaultTheme) {
      setTraditionalTheme(traditionalTheme, TRADITIONAL_THEME_LINK_ID)
    }
  }, [clearTheme, currentProperty, isAuthLoading, user, location.pathname, isSystemRoute])

  // 🚨 CRITICAL: Handle color scheme data changes (FIRST API CALL SUCCESS)
  useEffect(() => {
    if (colorSchemeData) {
      console.log('✅ Processing color scheme data - propertyId:', colorSchemeData.propertyId)
      try {
        yardiTranslationConfig.parameters = {
          propertyId: String(colorSchemeData.propertyId || '0'),
          productName: 'onlineleasingstudent'
        }
      } catch {}
      
      // Map response to PropertyInfo (matching Angular field mapping)
      const propertyInfo = mapThemeResponseToPropertyInfo(colorSchemeData)

      // Store in localStorage (matching Angular keys) - THIS IS CRITICAL FOR OTHER API CALLS
      if (currentProperty) {
        localStorage.setItem(localStorageVariables.customtheme, colorSchemeData.cssfileURL)
        localStorage.setItem(localStorageVariables.sitename, currentProperty)
        localStorage.setItem(localStorageVariables.PropertyId, colorSchemeData.propertyId)
        localStorage.setItem(localStorageVariables.Property, JSON.stringify(propertyInfo))
        localStorage.setItem(localStorageVariables.PropertyName, propertyInfo.name)
        localStorage.setItem('lastThemeUpdate', Date.now().toString()) // Track when theme was last updated
      }

      // Update state
      setState(prev => ({
        ...prev,
        siteName: currentProperty,
        cssUrl: colorSchemeData.cssfileURL,
        propertyInfo,
        isCustomTheme: true,
        propertyInfoLoaded: true,
        error: null
      }))

      // 🚨 CRITICAL: Set property info loaded status (equivalent to Angular BehaviorSubject.next(true))
      // This signals to other components that property information is now available
      propertyInfoStatusRef.current = true
      setPropertyInfoStatus(true)
      setIsInitialThemeLoadComplete(true)
      
      console.log('🎯 Property info status set to true - Banner/Logo API now enabled')
    }
  }, [colorSchemeData, currentProperty])

  // Handle banner and logo data changes
  useEffect(() => {
    if (bannerLogoData && String(bannerLogoData.status).toUpperCase() === 'OK') {
      if (bannerLogoData.BannerImageUrl || bannerLogoData.PropertyLogoUrl) {
        console.log('✅ Processing banner/logo data:', {
          BannerImageUrl: !!bannerLogoData.BannerImageUrl,
          PropertyLogoUrl: !!bannerLogoData.PropertyLogoUrl
        })
        
        // Store banner and logo URLs in localStorage (matching Angular behavior)
        if (bannerLogoData.BannerImageUrl) {
          localStorage.setItem(localStorageVariables.PropertyBannerImageUrl, bannerLogoData.BannerImageUrl)
        }
        if (bannerLogoData.PropertyLogoUrl) {
          localStorage.setItem(localStorageVariables.PropertyLogoUrl, bannerLogoData.PropertyLogoUrl)
          applyFavicon(bannerLogoData.PropertyLogoUrl)
        }

        // Update state
        setState(prev => ({
          ...prev,
          propertyLogoUrl: bannerLogoData.PropertyLogoUrl || prev.propertyLogoUrl,
          propertyBannerImageUrl: bannerLogoData.BannerImageUrl || prev.propertyBannerImageUrl
        }))
        
        console.log('🎉 Banner/Logo URLs successfully stored and state updated!')
      }
    }
  }, [bannerLogoData])

  // Handle CSS content changes
  useEffect(() => {
    if (cssContent) {
      try {
        applyCssStyles(cssContent, THEME_STYLE_ID)
      } catch (error) {
        console.error('Error applying CSS styles:', error)
        toast({
          title: "Theme Loading Error",
          description: "Failed to apply custom theme styles",
          variant: "destructive"
        })
      }
    }
  }, [cssContent, toast])

  // Handle loading states
  useEffect(() => {
    const isLoading = isLoadingColorScheme || isLoadingBannerLogo || isLoadingCSS
    setState(prev => ({
      ...prev,
      isLoading
    }))
  }, [isLoadingColorScheme, isLoadingBannerLogo, isLoadingCSS])

  // Handle errors
  useEffect(() => {
    const error = colorSchemeError || bannerLogoError || cssError
    if (error) {
      const errorMessage = error.message || 'Failed to load theme'
      console.error('Theme loading failed:', error)
      
      setState(prev => ({
        ...prev,
        error: errorMessage
      }))

      toast({
        title: "Theme Loading Error",
        description: errorMessage,
        variant: "destructive"
      })
    }
  }, [colorSchemeError, bannerLogoError, cssError, toast])

  // Handle new property detection - clear old theme when visiting a different property
  useEffect(() => {
    if (isNewProperty()) {
      clearTheme()
    }
  }, [isNewProperty, currentProperty, clearTheme])

  // Manual load theme function (for testing purposes)
  const loadTheme = useCallback(async (siteName: string) => {
    try {
      // Force clear current theme
      clearTheme()
      
      // Set the site name in localStorage temporarily so the query picks it up
      const previousSiteName = localStorage.getItem(localStorageVariables.sitename)
      localStorage.setItem(localStorageVariables.sitename, siteName)
      
      try {
        // Call the API directly
        const response = await getColorScheme(siteName)
        
        // Map response to PropertyInfo
        const propertyInfo = mapThemeResponseToPropertyInfo(response)

        // Store in localStorage
        localStorage.setItem(localStorageVariables.customtheme, response.cssfileURL)
        localStorage.setItem(localStorageVariables.PropertyId, response.propertyId)
        localStorage.setItem(localStorageVariables.Property, JSON.stringify(propertyInfo))
        localStorage.setItem(localStorageVariables.PropertyName, propertyInfo.name)
        localStorage.setItem('lastThemeUpdate', Date.now().toString()) // Track when theme was last updated

        // Update state
        setState(prev => ({
          ...prev,
          siteName,
          cssUrl: response.cssfileURL,
          propertyInfo,
          isCustomTheme: true,
          propertyInfoLoaded: true,
          error: null
        }))

        // Set property info loaded status
        propertyInfoStatusRef.current = true
        setPropertyInfoStatus(true)
        setIsInitialThemeLoadComplete(true)

        // Note: CSS fetching and applying is now handled by React Query useThemeCSS hook
        // No need to manually fetch CSS here to avoid duplicate requests
      } catch (error) {
        // Restore previous site name on error
        if (previousSiteName) {
          localStorage.setItem(localStorageVariables.sitename, previousSiteName)
        } else {
          localStorage.removeItem(localStorageVariables.sitename)
        }
        throw error
      }
    } catch (error) {
      console.error('❌ Manual theme load failed:', error)
      throw error
    }
  }, [clearTheme])

  // Manual banner and logo loading function
  const getBannerAndLogoUrl = useCallback(async (siteName: string, propertyId: string) => {
    try {
      const response = await getBannerAndPropertyLogoUrls(siteName, propertyId)
      
      if (response.status && response.BannerImageUrl && response.PropertyLogoUrl) {
        // Store in localStorage
        localStorage.setItem(localStorageVariables.PropertyBannerImageUrl, response.BannerImageUrl)
        localStorage.setItem(localStorageVariables.PropertyLogoUrl, response.PropertyLogoUrl)

        // Update state
        setState(prev => ({
          ...prev,
          propertyLogoUrl: response.PropertyLogoUrl,
          propertyBannerImageUrl: response.BannerImageUrl
        }))
      }
    } catch (error) {
      console.error('❌ Manual banner and logo load failed:', error)
      throw error
    }
  }, [])

  return {
    // State
    ...state,
    propertyInfoStatus,
    
    // Methods - INCLUDING CRITICAL NEW METHODS FOR API SEQUENCING
    loadTheme,
    clearTheme,
    getBannerAndLogoUrl,
    setTraditionalTheme: (theme: string) => setTraditionalTheme(theme, TRADITIONAL_THEME_LINK_ID),
    getTraditionalTheme: () => getTraditionalTheme(themeConfig.defaultTheme),
    getPropertyInformationStatus,
    
    // 🚨 CRITICAL: New methods for proper API call sequencing
    isThemeReady,
    waitForThemeReady,
    
    // Debug methods
    refetchColorScheme,
    refetchBannerLogo
  }
} 